import { Camera, Wifi, MapPin, Gauge } from "lucide-react";
import { Reveal } from "../lib/anim";

const CARDS = [
  {
    tag: "The Mapping Layer",
    title: "Drive & Record",
    desc: "Mount an approved dashcam, drive your usual routes, and automatically snap geolocated road images. Zero effort, always-on capture.",
    icon: Camera,
    accent: "#00B4D8",
    accentBg: "rgba(0,180,216,0.12)",
    img: "https://images.unsplash.com/photo-1584310881889-93bc9c4577ff",
    points: ["HD street imagery", "Auto geo-tagging", "Passive capture"],
    testid: "card-mapping-layer",
  },
  {
    tag: "The Bandwidth Layer",
    title: "Monetize Idle Internet",
    desc: "Securely share your leftover daily 4G/5G data packs to farm rewards every single second — even when your phone is in your pocket.",
    icon: Wifi,
    accent: "#4CAF50",
    accentBg: "rgba(76,175,80,0.12)",
    img: "https://images.unsplash.com/photo-1759210394481-61b92cdac046?crop=entropy&cs=srgb&fm=jpg&q=85",
    points: ["Encrypted sharing", "Runs in background", "Per-second rewards"],
    testid: "card-bandwidth-layer",
  },
];

export const DualEngine = () => {
  return (
    <section
      id="dual-engine"
      data-testid="dual-engine-section"
      className="relative py-24 md:py-32 bg-[#050d1a] border-y border-white/10"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <Reveal>
          <p className="font-mono-gx text-xs uppercase tracking-[0.25em] text-[#4CAF50] mb-4">
            / Dual-Engine Mining
          </p>
          <h2 className="font-display font-black text-3xl md:text-5xl tracking-tight max-w-3xl leading-[1.1]">
            Two engines. One app.{" "}
            <span className="text-slate-400">Twice the reasons to earn.</span>
          </h2>
        </Reveal>

        <div className="mt-14 grid md:grid-cols-2 gap-6 lg:gap-8">
          {CARDS.map((c, i) => {
            const Icon = c.icon;
            return (
              <Reveal key={c.title} delay={i * 0.1}>
                <div
                  data-testid={c.testid}
                  className="group relative h-full overflow-hidden rounded-3xl border border-white/10 bg-[#112240] transition-transform duration-300 hover:-translate-y-2"
                  style={{ boxShadow: `0 0 0 1px transparent` }}
                >
                  <div className="relative h-52 overflow-hidden">
                    <img
                      src={c.img}
                      alt={c.title}
                      className="h-full w-full object-cover opacity-50 transition-transform duration-700 group-hover:scale-110"
                    />
                    <div
                      className="absolute inset-0"
                      style={{
                        background: `linear-gradient(to top, #112240 8%, transparent 90%)`,
                      }}
                    />
                    <div
                      className="absolute top-5 left-5 flex h-12 w-12 items-center justify-center rounded-xl"
                      style={{ background: c.accentBg, border: `1px solid ${c.accent}55` }}
                    >
                      <Icon size={22} style={{ color: c.accent }} />
                    </div>
                  </div>

                  <div className="p-8 md:p-10">
                    <div
                      className="font-mono-gx text-xs uppercase tracking-[0.2em] mb-3"
                      style={{ color: c.accent }}
                    >
                      {c.tag}
                    </div>
                    <h3 className="font-display font-bold text-2xl md:text-3xl tracking-tight mb-4">
                      {c.title}
                    </h3>
                    <p className="text-slate-400 leading-relaxed mb-6">{c.desc}</p>
                    <ul className="flex flex-wrap gap-2.5">
                      {c.points.map((p) => (
                        <li
                          key={p}
                          className="rounded-full border border-white/10 bg-white/5 px-3.5 py-1.5 text-xs text-slate-300"
                        >
                          {p}
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div
                    className="absolute inset-x-0 bottom-0 h-[3px] scale-x-0 transition-transform duration-500 group-hover:scale-x-100 origin-left"
                    style={{ background: c.accent }}
                  />
                </div>
              </Reveal>
            );
          })}
        </div>

        {/* mini stats strip */}
        <Reveal delay={0.15}>
          <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { icon: MapPin, k: "Geo-verified", v: "every capture" },
              { icon: Gauge, k: "Real-time", v: "reward ticker" },
              { icon: Wifi, k: "Bank-grade", v: "encryption" },
              { icon: Camera, k: "Approved", v: "hardware only" },
            ].map((s) => {
              const I = s.icon;
              return (
                <div
                  key={s.k}
                  className="flex items-center gap-3 rounded-2xl border border-white/10 bg-white/[0.02] px-5 py-4"
                >
                  <I size={18} className="text-[#00B4D8] shrink-0" />
                  <div>
                    <div className="text-sm font-semibold">{s.k}</div>
                    <div className="text-xs text-slate-500">{s.v}</div>
                  </div>
                </div>
              );
            })}
          </div>
        </Reveal>
      </div>
    </section>
  );
};
